/**
 * The head match and the qualifier rungs (plan §6.3), split from
 * `resolve-entity-phrase.ts` at the 500-line cap (D226): the longest-tail
 * head match, the adjective and page rungs that filter its candidates, the
 * stranded-name rescue, and the normalizations both sides of a comparison
 * share. The orchestrator in `resolve-entity-phrase.ts` owns the order the
 * rungs run in; this module owns what each rung is.
 *
 * @packageDocumentation
 */

import { foldDiacritics } from '../normalize/fold-characters.js';
import { canonicalizeNumerals } from '../normalize/numerals.js';
import { type ScoredEntityCandidate } from '../rank/select-or-clarify.js';
import { CHAIN_CONJUNCTION_TOKENS } from '../tokenize/split-on-conjunctions.js';
import { type ClarificationCandidate, type ParseCandidateEntity } from '../types.js';

/**
 * Words that ride inside a phrase without filtering or surfacing: the
 * coordinating conjunctions (D81) and the possessive determiners (D205).
 * The head is never one of these — the tail is matched before they are
 * dropped, so a multiword alias like `my hands` (D63) still meets a typed
 * `my hands` whole.
 *
 * Exported for the audit (D210): a phrase window that opens with one of
 * these would be judged as if the word were an adjective, and the resolver
 * throws it away — so the audit ends its runs on them, reading the list
 * from here rather than keeping a copy that would drift.
 */
export const TRANSPARENT_QUALIFIER_WORDS: ReadonlySet<string> = new Set([
	...CHAIN_CONJUNCTION_TOKENS,
	'my',
	'your',
	'his',
	'her',
	'its',
	'their',
	'our',
]);

/** Every candidate matching the longest head, plus the leftover adjective words (transparent words already dropped — D81, D205). */
export interface HeadMatch {
	readonly candidates: readonly ScoredEntityCandidate[];
	/** D227: how many trailing words the head spans — what a longer, claimed tail is measured against. */
	readonly headWordCount: number;
	readonly qualifierWords: readonly string[];
}

/**
 * How many distinct entities an interior rescue may offer before the phrase
 * is too ambiguous to ask about and fails honestly instead (D26).
 */
const INTERIOR_RESCUE_CANDIDATE_CAP = 3;

/**
 * Find a catalog name stranded anywhere in a word sequence (D26, generalized
 * for D29's question routing) — rightmost end position first, longest name
 * first, so "the sealed crate thing" asks about the crate, not "sealed".
 *
 * @param phraseWords - The surface words, in order
 * @param entities - The parse catalog
 * @returns Clarification candidates for the winning name (empty when none, or when too many entities share it)
 */
export const findStrandedEntities = (
	phraseWords: readonly string[],
	entities: readonly ParseCandidateEntity[],
): readonly ClarificationCandidate[] => {
	const longestName = longestNameWordCount(entities);

	// End positions right to left, tail included (a caller whose tail already
	// failed head-matching simply finds nothing there and walks inward).
	for (let endIndex = phraseWords.length; endIndex >= 1; endIndex -= 1) {
		for (let nameWordCount = Math.min(endIndex, longestName); nameWordCount >= 1; nameWordCount -= 1) {
			const interiorText = phraseWords.slice(endIndex - nameWordCount, endIndex).join(' ');
			const matchingEntities = entities.filter((entity) => matchEntityName(entity, canonicalizeNumerals(interiorText)) !== undefined);

			if (matchingEntities.length > INTERIOR_RESCUE_CANDIDATE_CAP) {
				return [];
			}

			if (matchingEntities.length > 0) {
				return matchingEntities.map((entity) => ({
					entityRef: entity.entityRef,
					matchedTokens: [interiorText],
				}));
			}
		}
	}

	return [];
};

/**
 * Keep only the candidates whose adjective lists contain EVERY qualifier word
 * (plan §6.3: an adjective matching no candidate's list eliminates it).
 *
 * @param candidates - The head-matched candidates
 * @param qualifierWords - The phrase words before the head
 * @returns The surviving candidates
 */
export const filterByAdjectives = (
	candidates: readonly ScoredEntityCandidate[],
	qualifierWords: readonly string[],
): readonly ScoredEntityCandidate[] =>
	candidates.filter((candidate) =>
		qualifierWords.every((qualifierWord) =>
			qualifierFormsOf(qualifierWord).some((form) =>
				candidate.entity.adjectives.some((adjective) => normalizeCatalogName(adjective) === form),
			),
		),
	);

/**
 * D205: keep only the candidates whose OWN prose supplies whatever their
 * adjectives do not — every qualifier word is a declared adjective or a word
 * of the entity's description. Reached only below the adjective rung; a
 * catalog carrying no description words lets nobody through, so a caller
 * that supplies none sees exactly the pre-D205 parser.
 *
 * @param candidates - The head-matched candidates
 * @param qualifierWords - The phrase words before the head
 * @returns The candidates whose page said the words
 */
export const filterByDescription = (
	candidates: readonly ScoredEntityCandidate[],
	qualifierWords: readonly string[],
): readonly ScoredEntityCandidate[] =>
	candidates.filter((candidate) => {
		const describedWords = [...candidate.entity.adjectives, ...(candidate.entity.descriptionWords ?? [])].map(normalizeCatalogName);

		return qualifierWords.every((qualifierWord) => qualifierFormsOf(qualifierWord).some((form) => describedWords.includes(form)));
	});

/**
 * The forms a qualifier word may be matched in: as typed, and — for a
 * possessive — as its owner (D216, Calder's `the-hologram-is-the-fallback`
 * §3a).
 *
 * @remarks
 * A page says "the estate", "the village", "the peristyle", and a player
 * points at their things with the possessive: `examine the estate's papers`,
 * `drink the village's water`. D205 made `my`/`your` transparent because the
 * catalog models no owner, and the same is true here — the owner cannot
 * select anything the catalog does not already know — so the possessive is
 * read as the word it is built on. The word as typed is tried first, so a
 * page that itself says `mill's` still meets `mill's`. Plural owners fold the
 * same way: `villagers'` is `villagers`. Exported for the phrase corpus's
 * deriver, which states the resolver's rungs and must read a possessive the
 * way the resolver does.
 *
 * @param qualifierWord - One word before the head
 * @returns Its forms, the typed form first
 */
export const qualifierFormsOf = (qualifierWord: string): readonly string[] => {
	const typed = canonicalizeNumerals(qualifierWord);
	const owner = typed.replace(/'s$|(?<=s)'$/u, '');

	return owner === typed ? [typed] : [typed, owner];
};

/**
 * How many words the catalog's longest noun/alias spans — the ceiling for
 * head-tail matching.
 *
 * @param entities - The parse catalog
 * @returns The longest name's word count (0 for an empty catalog)
 */
const longestNameWordCount = (entities: readonly ParseCandidateEntity[]): number => {
	let longestWordCount = 0;

	for (const entity of entities) {
		for (const name of [...entity.nouns, ...entity.aliases]) {
			longestWordCount = Math.max(longestWordCount, normalizeCatalogName(name).split(' ').length);
		}
	}

	return longestWordCount;
};

/**
 * Find the longest phrase tail matching any entity's noun/alias, and every
 * entity that matches it.
 *
 * @param phraseWords - The phrase's surface words, in order
 * @param entities - The parse catalog
 * @returns The head match, or undefined when no tail matches anything
 */
export const matchLongestHead = (
	phraseWords: readonly string[],
	entities: readonly ParseCandidateEntity[],
): HeadMatch | undefined => {
	const maxHeadWordCount = Math.min(phraseWords.length, longestNameWordCount(entities));

	for (let headWordCount = maxHeadWordCount; headWordCount >= 1; headWordCount -= 1) {
		const headText = phraseWords.slice(phraseWords.length - headWordCount).join(' ');
		// D133: numerals compare in canonical form on both sides (`eleven` and
		// `11` are one token here); the head SURFACES as typed.
		const headKey = canonicalizeNumerals(headText);
		// D81 (Calder's D1): conjunctions are TRANSPARENT among qualifiers —
		// "the pinned and faded roster" is ordinary English, and the splitter
		// keeps that `and` inside the phrase precisely so coordination can
		// read. The word neither filters (it is no adjective) nor surfaces as
		// a consumed qualifier; the adjectives on either side of it do both,
		// exactly as if juxtaposed. D205 adds the possessives to the same set
		// ("examine my cases").
		const qualifierWords = phraseWords
			.slice(0, phraseWords.length - headWordCount)
			.filter((qualifierWord) => !TRANSPARENT_QUALIFIER_WORDS.has(qualifierWord));
		const candidates: ScoredEntityCandidate[] = [];

		for (const entity of entities) {
			const matchKind = matchEntityName(entity, headKey);

			if (matchKind !== undefined) {
				candidates.push({
					entity,
					matchedName: headText,
					matchKind,
					qualifiers: qualifierWords,
					score: entity.salience,
				});
			}
		}

		if (candidates.length > 0) {
			return { candidates, headWordCount, qualifierWords };
		}
	}

	return undefined;
};

/**
 * D227 (Calder's `the-road-climbs` §4): does the matched head, read leftward
 * through the transparent words directly before it, spell a name the world
 * claims for a thing that is NOT here? A transparent word is not transparent
 * then: `her coin`, with her coin in another room, is HER COIN — absent — and
 * never `coin`, the island's, which was the one coin the player was trying
 * not to touch. The claimant present would have won the longer tail outright
 * in {@link matchLongestHead}; absent, its name still holds the words. Only
 * transparent words extend the head — a declared adjective is the present
 * thing's own (`dull coin`, with a dull coin here, is that coin whatever else
 * is called `dull coin` elsewhere), and a word that is neither eliminates
 * everyone anyway.
 *
 * @param phraseWords - The phrase's surface words, in order
 * @param headMatch - The head the catalog matched
 * @param claimedNames - Every multiword name the world claims, as authored (empty: no world beyond the catalog)
 * @returns Whether a longer, claimed tail outranks this head
 */
export const isClaimedThroughTransparentWords = (
	phraseWords: readonly string[],
	headMatch: HeadMatch,
	claimedNames: ReadonlySet<string>,
): boolean => {
	if (claimedNames.size === 0) {
		return false;
	}

	const normalizedClaims = new Set([...claimedNames].map(normalizeCatalogName));
	const headStart = phraseWords.length - headMatch.headWordCount;
	// The run of transparent words directly before the head, read outward from it.
	const transparentRun = phraseWords.slice(0, headStart).reverse().findIndex((word) => !TRANSPARENT_QUALIFIER_WORDS.has(word));
	const extendableBy = transparentRun === -1 ? headStart : transparentRun;

	for (let extraWords = 1; extraWords <= extendableBy; extraWords += 1) {
		if (normalizedClaims.has(canonicalizeNumerals(phraseWords.slice(headStart - extraWords).join(' ')))) {
			return true;
		}
	}

	return false;
};

/**
 * Does this head text match the entity — and via canonical noun or alias?
 *
 * @param entity - The catalog entity
 * @param headText - The candidate head text (folded)
 * @returns The match kind, or undefined when nothing matches
 */
const matchEntityName = (entity: ParseCandidateEntity, headText: string): 'alias' | 'noun' | undefined => {
	if (entity.nouns.some((noun) => normalizeCatalogName(noun) === headText)) {
		return 'noun';
	}

	if (entity.aliases.some((alias) => normalizeCatalogName(alias) === headText)) {
		return 'alias';
	}

	return undefined;
};

/**
 * Trim + lowercase one catalog name at comparison time — the same generous
 * intake `compileLexicon` applies to authored vocabulary, mirrored here for
 * caller-supplied catalogs (deliberate parallel, not extractable: each side
 * owns its intake contract) — and its numerals canonical (D133), so an
 * authored `locker eleven` meets a typed `locker 11` on equal terms. D226
 * (E13) folds its diacritics the way `foldCharacters` folds the player's, so
 * an authored `léo` meets a typed `leo`.
 *
 * @param name - The authored catalog name
 * @returns The normalized name
 */
const normalizeCatalogName = (name: string): string => canonicalizeNumerals(foldDiacritics(name.trim().toLowerCase()));
